import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone, MapPin } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1760670399462-f5e479452c27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxJVCUyMHRlY2hub2xvZ3klMjBzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGxhcHRvcCUyMGNvZGluZ3xlbnwwfHx8fDE3NzI0NDAyNjR8MA&ixlib=rb-4.1.0&q=85';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch (err) {
      console.error('Submit error:', err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div data-testid="contact-page">
      <PageHeader caption="Get in Touch" title="Contact Us" subtitle="We are here to help. Reach out to discuss your staffing requirements." bgImage={HEADER_BG} />

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="sol-caption mb-4">Reach Us</p>
              <h2 className="text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Let's Start a Conversation</h2>
              <p className="text-[#525252] leading-relaxed mb-10">Solnity is a Canada-based IT Staffing and Talent Solutions provider. Whether you are an enterprise client looking to hire, a staffing firm interested in partnership, or a candidate seeking opportunities, our team is ready to assist.</p>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] shrink-0"><Mail className="w-5 h-5 text-black" strokeWidth={1.5} /></div>
                  <div><h4 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Email</h4><p className="text-sm text-[#525252]">support@solnity.com</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] shrink-0"><Phone className="w-5 h-5 text-black" strokeWidth={1.5} /></div>
                  <div><h4 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Phone</h4><p className="text-sm text-[#525252]">226-799-8794</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] shrink-0"><MapPin className="w-5 h-5 text-black" strokeWidth={1.5} /></div>
                  <div><h4 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Headquarters</h4><p className="text-sm text-[#525252]">Scarborough,<br />Ontario, M1H 2M9, Canada</p></div>
                </div>
              </div>
            </div>
            <div className="bg-[#F5F5F5] p-8 md:p-12">
              {submitted ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Thank You</h3>
                  <p className="text-[#525252] mb-6">Your message has been received. Our team will get back to you within 24 hours.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', company: '', phone: '', subject: '', message: '' }); }} className="sol-btn-outline text-sm">Send Another Message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Send a Message</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div><label className="block text-sm font-medium text-[#333333] mb-2">Full Name *</label><input name="name" value={form.name} onChange={handleChange} required className="sol-input" placeholder="John Smith" data-testid="contact-name-input" /></div>
                    <div><label className="block text-sm font-medium text-[#333333] mb-2">Email Address *</label><input name="email" type="email" value={form.email} onChange={handleChange} required className="sol-input" placeholder="john@company.com" data-testid="contact-email-input" /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div><label className="block text-sm font-medium text-[#333333] mb-2">Company</label><input name="company" value={form.company} onChange={handleChange} className="sol-input" placeholder="Company Name" data-testid="contact-company-input" /></div>
                    <div><label className="block text-sm font-medium text-[#333333] mb-2">Phone</label><input name="phone" value={form.phone} onChange={handleChange} className="sol-input" placeholder="+1 (555) 000-0000" data-testid="contact-phone-input" /></div>
                  </div>
                  <div className="mb-4"><label className="block text-sm font-medium text-[#333333] mb-2">Subject *</label><input name="subject" value={form.subject} onChange={handleChange} required className="sol-input" placeholder="How can we help?" data-testid="contact-subject-input" /></div>
                  <div className="mb-6"><label className="block text-sm font-medium text-[#333333] mb-2">Message *</label><textarea name="message" value={form.message} onChange={handleChange} required className="sol-textarea" placeholder="Tell us about your requirements..." data-testid="contact-message-input" /></div>
                  <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="sol-btn-primary w-full text-sm">
                    {loading ? 'Sending...' : 'Send Message'} <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Prefer to Talk?</h2>
          <p className="text-white/60 mb-8">Schedule a consultation with one of our staffing experts.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/hire-talent" className="sol-btn-inverse text-sm">Hire Talent <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/become-vendor-partner" className="sol-btn-inverse text-sm">Become a Vendor Partner <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
